<script setup lang="ts">
import {
  TASK_NOTIFY_MESSAGE_MAX,
  taskNotifyFirstName,
  type TaskNotifyChannel,
  type TaskNotifyOptions,
} from '@cockpit/shared';
import { computed, useId } from 'vue';
import { channelAvailable } from '../../taskNotify';

/**
 * Lot AZ « Le prévenir » (maquette, formulaire de tâche) : « Prévenir Inès » dès que « Pour qui » n'est pas soi,
 * décoché par défaut ; Google Chat ou mail ; message prérempli et modifiable, le lien vers la tâche est ajouté à
 * la fin. Sans réseau, la case est grisée « Disponible avec le réseau ».
 */
const props = defineProps<{
  name: string;
  on: boolean;
  channel: TaskNotifyChannel;
  message: string;
  options: TaskNotifyOptions | null;
  online: boolean;
  disabled?: boolean;
}>();
const emit = defineEmits<{
  'update:on': [value: boolean];
  'update:channel': [value: TaskNotifyChannel];
  'update:message': [value: string];
}>();

const uid = useId();
const first = computed(() => taskNotifyFirstName(props.name));
const chatOk = computed(() => channelAvailable(props.options, 'chat'));
const mailOk = computed(() => channelAvailable(props.options, 'email'));
const noChannel = computed(() => !chatOk.value && !mailOk.value);
</script>

<template>
  <div class="task-notify" data-testid="task-notify">
    <label class="tn-on">
      <input
        type="checkbox"
        :checked="on && online"
        :disabled="disabled || !online || noChannel"
        data-testid="task-notify-on"
        @change="emit('update:on', ($event.target as HTMLInputElement).checked)"
      />
      <span
        >Prévenir <b>{{ first }}</b></span
      >
    </label>
    <p v-if="!online" class="na-note" data-testid="task-notify-offline">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
        <path d="M2 6a9 9 0 0 1 12 0M4.5 8.8a5.5 5.5 0 0 1 7 0M2 2l12 12" stroke-linecap="round" /></svg
      >Disponible avec le réseau
    </p>
    <p v-else-if="noChannel" class="tn-hint">
      {{ options?.chat.reason }} <RouterLink to="/compte-google">Mon compte Google</RouterLink> ·
      {{ options?.email.reason }}
    </p>
    <template v-else-if="on">
      <div class="tn-ch" role="radiogroup" :aria-label="`Prévenir ${first} par`">
        <label :class="{ off: !chatOk }">
          <input
            type="radio"
            :name="`tn-${uid}`"
            value="chat"
            :checked="channel === 'chat'"
            :disabled="disabled || !chatOk"
            data-testid="task-notify-chat"
            @change="emit('update:channel', 'chat')"
          />
          Google Chat
        </label>
        <label :class="{ off: !mailOk }">
          <input
            type="radio"
            :name="`tn-${uid}`"
            value="email"
            :checked="channel === 'email'"
            :disabled="disabled || !mailOk"
            data-testid="task-notify-email"
            @change="emit('update:channel', 'email')"
          />
          Mail
        </label>
      </div>
      <p v-if="!chatOk" class="tn-hint" data-testid="task-notify-chat-off">
        {{ options?.chat.reason }} <RouterLink to="/compte-google">Mon compte Google</RouterLink>
      </p>
      <p v-if="!mailOk" class="tn-hint">{{ options?.email.reason }}</p>
      <textarea
        :value="message"
        rows="2"
        :maxlength="TASK_NOTIFY_MESSAGE_MAX"
        aria-label="Message"
        :disabled="disabled"
        data-testid="task-notify-message"
        @input="emit('update:message', ($event.target as HTMLTextAreaElement).value)"
      ></textarea>
      <p class="tn-hint">
        {{
          channel === 'chat'
            ? `Message privé Google Chat envoyé en ton nom.`
            : `Mail depuis contact@evolusite.fr, à l’adresse Evolusite de ${first}.`
        }}
        Le lien vers la tâche est ajouté à la fin.
      </p>
    </template>
  </div>
</template>
